import { CompactText } from 'ui/CompactText';
import { useCallback, useEffect, useId, useRef, type KeyboardEvent } from 'react';
import IMask from 'imask';

import {
  clampWalletAmountRub,
  WALLET_AMOUNT_MAX_RUB,
  WALLET_AMOUNT_MIN_RUB,
} from './walletAmountLimits';

import styles from './WalletRubAmountInput.module.scss';

const STEP_BUTTON_RUB = 1;
const STEP_ARROW_RUB = 1;

type WalletRubAmountInputProps = {
  id?: string;
  label?: string;
  value: number | '';
  onChange: (value: number | '') => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  hint?: string;
  placeholder?: string;
};

export const WalletRubAmountInput = ({
  id,
  label = 'Сумма',
  value,
  onChange,
  min = WALLET_AMOUNT_MIN_RUB,
  max = WALLET_AMOUNT_MAX_RUB,
  disabled,
  hint,
  placeholder = '1 000,00',
}: WalletRubAmountInputProps) => {
  const autoId = useId();
  const inputId = id ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);
  const maskRef = useRef<ReturnType<typeof IMask> | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const applyStep = useCallback(
    (delta: number) => {
      const base = value === '' ? min : value;
      const next = clampWalletAmountRub(base + delta);
      onChangeRef.current(next);
      if (maskRef.current) {
        maskRef.current.typedValue = next;
      }
    },
    [min, value],
  );

  useEffect(() => {
    if (!inputRef.current) return undefined;

    maskRef.current?.destroy();
    const mask = IMask(inputRef.current, {
      mask: Number,
      scale: 2,
      thousandsSeparator: ' ',
      radix: ',',
      mapToRadix: ['.'],
      min,
      max,
      lazy: false,
      normalizeZeros: true,
      padFractionalZeros: false,
    });
    maskRef.current = mask;

    if (value !== '') {
      mask.typedValue = value;
    }

    mask.on('accept', () => {
      if (!mask.unmaskedValue) {
        onChangeRef.current('');
        return;
      }
      onChangeRef.current(clampWalletAmountRub(Number(mask.typedValue)));
    });

    return () => mask.destroy();
  }, [max, min]);

  useEffect(() => {
    const mask = maskRef.current;
    if (!mask) return;

    if (value === '') {
      if (mask.unmaskedValue) mask.typedValue = '';
      return;
    }

    const current = Number(mask.typedValue);
    if (!Number.isFinite(current) || Math.abs(current - value) > 0.001) {
      mask.typedValue = value;
    }
  }, [value]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
    event.preventDefault();
    applyStep(event.key === 'ArrowUp' ? STEP_ARROW_RUB : -STEP_ARROW_RUB);
  };

  const defaultHint = `От ${min.toLocaleString('ru-RU')} до ${max.toLocaleString('ru-RU')} ₽, можно указать копейки`;

  return (
    <div className={styles.wrap}>
      <label className={styles.label} htmlFor={inputId}>
        {label}
      </label>
      <div className={styles.control}>
        <button
          type='button'
          className={styles.stepBtn}
          aria-label='Уменьшить сумму на 1 рубль'
          disabled={disabled || (value !== '' && value <= min)}
          onClick={() => applyStep(-STEP_BUTTON_RUB)}
        >
          −
        </button>
        <div className={styles.inputWrap}>
          <input
            id={inputId}
            ref={inputRef}
            className={styles.input}
            inputMode='decimal'
            autoComplete='off'
            placeholder={placeholder}
            disabled={disabled}
            onKeyDown={handleKeyDown}
          />
          <span className={styles.suffix} aria-hidden>
            ₽
          </span>
        </div>
        <button
          type='button'
          className={styles.stepBtn}
          aria-label='Увеличить сумму на 1 рубль'
          disabled={disabled || (value !== '' && value >= max)}
          onClick={() => applyStep(STEP_BUTTON_RUB)}
        >
          +
        </button>
      </div>
      <CompactText className={styles.hint} tone={'info'}>{hint ?? defaultHint}</CompactText>
    </div>
  );
};
